import React, { useEffect, useState, useCallback } from "react";
import { adminApi, TOKEN_KEY } from "./adminApi";
import { ConfirmDeleteModal } from "./PapersPanel";
import {
  Mail,
  RefreshCw,
  Download,
  ExternalLink,
  Loader2,
  Trash2,
} from "lucide-react";

const PRODUCT_LABELS = {
  all: "All",
  kainos: "Kainos AI",
  grantpilot: "GrantPilot AI",
};

const PRODUCT_ACCENTS = {
  kainos: "#111827",
  grantpilot: "#00BCD4",
};

const WaitlistPanel = () => {
  const [data, setData] = useState(null);
  const [product, setProduct] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchSignups = useCallback(async (p) => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (p && p !== "all") params.product = p;
      const res = await adminApi.get("/admin/waitlist/signups", { params });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignups(product);
  }, [product, fetchSignups]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      await adminApi.delete(`/admin/waitlist/signups/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchSignups(product);
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const downloadCsv = () => {
    if (!data?.signups?.length) return;
    const rows = [
      ["Product", "Name", "Email", "ORCID", "Affiliation", "Research Interests", "Timestamp"],
      ...data.signups.map((s) => [
        s.product || "",
        s.name,
        s.email,
        s.orcid || "",
        s.affiliation || "",
        (s.research_interests || "").replace(/\n/g, " "),
        s.timestamp,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kai-waitlist-${product}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const signups = data?.signups || [];
  const counts = data?.counts_by_product || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="admin-waitlist-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light text-gray-900 tracking-tight">
            Waitlist Signups
            <span className="ml-3 text-sm text-gray-400 font-mono">
              {data?.total ?? 0} / {totalAll}
            </span>
          </h2>
          <p className="text-sm text-gray-500 font-light mt-1">
            Professors and researchers who have joined a product waitlist.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchSignups(product)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-gray-200 rounded-md hover:border-gray-900 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={!signups.length}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-gray-900 text-white rounded-md hover:bg-black transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {["all", "kainos", "grantpilot"].map((key) => {
          const active = product === key;
          const count = key === "all" ? totalAll : counts[key] || 0;
          return (
            <button
              key={key}
              onClick={() => setProduct(key)}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-tight transition-colors ${
                active ? "text-gray-900" : "text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              {PRODUCT_ACCENTS[key] && (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: PRODUCT_ACCENTS[key] }}
                />
              )}
              {PRODUCT_LABELS[key]}
              <span className="text-[10px] font-mono text-gray-400">{count}</span>
              {active && (
                <span className="absolute left-2 right-2 -bottom-px h-px bg-gray-900" />
              )}
            </button>
          );
        })}
      </div>

      {signups.length === 0 ? (
        <div className="text-center py-20 bg-white border border-dashed border-gray-200 rounded-xl">
          <Mail className="w-8 h-8 text-gray-300 mx-auto mb-4" />
          <p className="text-sm text-gray-500 font-light">
            No signups yet. Entries will appear here once submitted.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500">
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Product</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">ORCID</th>
                  <th className="px-5 py-3">Affiliation</th>
                  <th className="px-5 py-3">Joined</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {signups.map((s, i) => (
                  <tr key={s.id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                      {String(i + 1).padStart(2, "0")}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] rounded-full bg-gray-50 border border-gray-200"
                        style={{ color: PRODUCT_ACCENTS[s.product] || "#374151" }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: PRODUCT_ACCENTS[s.product] || "#9CA3AF" }}
                        />
                        {PRODUCT_LABELS[s.product] || s.product || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-900">{s.name}</td>
                    <td className="px-5 py-3 text-gray-700">
                      <a href={`mailto:${s.email}`} className="hover:underline text-[#00BCD4]">
                        {s.email}
                      </a>
                    </td>
                    <td className="px-5 py-3 text-gray-600 font-mono text-xs">
                      {s.orcid ? (
                        <a
                          href={`https://orcid.org/${s.orcid}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          {s.orcid}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {s.affiliation || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs whitespace-nowrap">
                      {new Date(s.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setPendingDelete(s)}
                        disabled={deletingId === s.id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                        aria-label="Delete signup"
                      >
                        {deletingId === s.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDeleteModal
          label="signup"
          preview={`${pendingDelete.name} · ${pendingDelete.email}`}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          loading={deletingId === pendingDelete.id}
        />
      )}
    </div>
  );
};

export default WaitlistPanel;
